import * as api from "./client.js";

/**
 * The source-data panel's three staff-bff reads: key stats, L500 Live and testimonials for one firm in one
 * edition and research BPA. Types mirror the staff-bff route contracts field for field.
 */

/** Why a route could not look the data up. The four known values, but any string can arrive. */
export type UnresolvedReason =
  | "no_firm_ref"
  | "no_company_wp_id"
  | "no_edition_wp_id"
  | "no_bpa_wp_id";

export type SourceDataScope = {
  companyUuid: string;
  editionId: string;
  researchBpaId: string;
};

export type KeyStats = {
  submitted: boolean | null;
  refereesSubmitted: number | null;
  refereesResponded: number | null;
};

export type KeyStatsResponse = {
  unresolvedReason: UnresolvedReason | string | null;
  keyStats: KeyStats;
};

export type RollingSubmissionClient = {
  id: string;
  rollingSubmissionId: string;
  position: number;
  clientCompanyName: string | null;
  clientIndustry: string | null;
  clientEngagementDate: string | null;
  clientPublish: string | null;
  clientCreated: string | null;
  clientModified: string | null;
  modifiedBy: string | null;
  status: string | null;
};

export type RollingSubmissionMatter = {
  id: string;
  rollingSubmissionId: string;
  position: number;
  matterCompanyName: string | null;
  matterSummary: string | null;
  matterDate: string | null;
  matterCreated: string | null;
  matterModified: string | null;
  modifiedBy: string | null;
  status: string | null;
};

export type RollingSubmissionTeamActive = {
  id: string;
  rollingSubmissionId: string;
  position: number;
  activeName: string | null;
  activeRole: string | null;
  activeJobTitle: string | null;
  activeEmail: string | null;
  activeLocation: string | null;
  activeJoined: string | null;
  activePreviousFirm: string | null;
  activeCreated: string | null;
  activeModified: string | null;
  modifiedBy: string | null;
  status: string | null;
};

export type RollingSubmissionTeamDeparted = {
  id: string;
  rollingSubmissionId: string;
  position: number;
  departedName: string | null;
  departedRole: string | null;
  departedJobTitle: string | null;
  departedDate: string | null;
  departedFirmJoined: string | null;
  departedCreated: string | null;
  departedModified: string | null;
  modifiedBy: string | null;
  status: string | null;
};

export type RollingSubmission = {
  id: string;
  firmRef: string | null;
  companyName: string | null;
  officeName: string | null;
  companyUuid: string;
  officeUuid: string | null;
  contactUuid: string | null;
  publicationId: number;
  publicationName: string | null;
  practiceAreaId: number;
  practiceAreaName: string | null;
  practiceAreaSlug: string | null;
  practiceGroupId: number | null;
  practiceGroupName: string | null;
  editorialCategoryId: number | null;
  region: string | null;
  country: string | null;
  countryId: number | null;
  tableName: string | null;
  status: string | null;
  approvalStatus: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  visibility: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  clients: RollingSubmissionClient[];
  matters: RollingSubmissionMatter[];
  teamActive: RollingSubmissionTeamActive[];
  teamDeparted: RollingSubmissionTeamDeparted[];
};

export type L500LiveResponse = {
  unresolvedReason: UnresolvedReason | string | null;
  rollingSubmissions: RollingSubmission[] | null;
};

export type Testimonial = {
  id: string;
  externalId: string | null;
  content: string;
  language: string;
  publicationId: number;
  practiceAreaId: number | null;
  lawyerName: string | null;
  firmRef: string;
  status: string;
  companyId: number | null;
  surveyResponseId: string | null;
  order: number | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type TestimonialsResponse = {
  unresolvedReason: UnresolvedReason | string | null;
  testimonials: Testimonial[] | null;
};

/** The `sendError` body every non-200 answer carries. */
export type SourceDataErrorBody = {
  error: string;
  code: string;
  request_id: string;
  requestId: string;
  details?: { field: string; message: string }[];
};

function read<T>(route: string, scope: SourceDataScope): Promise<T> {
  const company = encodeURIComponent(scope.companyUuid);
  const edition = encodeURIComponent(scope.editionId);
  const bpa = encodeURIComponent(scope.researchBpaId);
  return api.get<T>(
    `/v1/research/companies/${company}/${route}?editionId=${edition}&researchBpaId=${bpa}`,
  );
}

export const sourceData = {
  getKeyStats(scope: SourceDataScope): Promise<KeyStatsResponse> {
    return read<KeyStatsResponse>("key-stats", scope);
  },

  getL500Live(scope: SourceDataScope): Promise<L500LiveResponse> {
    return read<L500LiveResponse>("l500-live", scope);
  },

  getTestimonials(scope: SourceDataScope): Promise<TestimonialsResponse> {
    return read<TestimonialsResponse>("testimonials", scope);
  },
};
